/* Flycatcher Bundle Builder UI v2 */

(() => {
  if (window.FlycatcherBundleBuilder) return;

  class FlycatcherBundleBuilder {
    constructor(root) {
      this.root = root;
      document.documentElement.classList.add('bundle-builder-template');
      this.config = this.readConfig();

      if (!this.config || !this.config.primaryProducts?.length) return;

      this.state = {
        step: 1,
        primaryHandle: '',
        primaryVariantId: null,
        addons: new Map(),
        category: 'all',
        summaryExpanded: false
      };

      this.cacheElements();
      this.bindEvents();

      const returnedFromCheckout = this.consumeCheckoutPending();

      if (!returnedFromCheckout) {
        this.restoreState();
      }

      this.render();
    }

    readConfig() {
      const element = this.root.querySelector('[data-bundle-config]');

      if (!element) return null;

      try {
        return JSON.parse(element.textContent);
      } catch (error) {
        console.error('Bundle Builder: invalid JSON configuration.', error);
        return null;
      }
    }

    cacheElements() {
      this.primaryGrid = this.root.querySelector('[data-primary-grid]');
      this.addonGroups = this.root.querySelector('[data-addon-groups]');
      this.summary = this.root.querySelector('[data-summary]');
      this.summaryItems = this.root.querySelector('[data-summary-items]');
      this.mobileSummaryToggle = this.root.querySelector('[data-mobile-summary-toggle]');
      this.mobileSummaryPreview = this.root.querySelector('[data-mobile-summary-preview]');
      this.mobileSummaryTotal = this.root.querySelector('[data-mobile-summary-total]');
      this.mobileSummaryOriginalTotal = this.root.querySelector('[data-mobile-summary-original-total]');
      this.mobileRewardBadge = this.root.querySelector('[data-mobile-reward-badge]');
      this.desktopRewardBadge = this.root.querySelector('[data-desktop-reward-badge]');
      this.desktopSummaryTotal = this.root.querySelector('[data-desktop-summary-total]');
      this.desktopCurrentTotal = this.root.querySelector('[data-desktop-current-total]');
      this.desktopOriginalTotal = this.root.querySelector('[data-desktop-original-total]');
      this.mobileBackButton = this.root.querySelector('[data-mobile-back-button]');
      this.reward = this.root.querySelector('[data-reward]');
      this.subtotal = this.root.querySelector('[data-subtotal]');
      this.savings = this.root.querySelector('[data-savings]');
      this.savingsRow = this.root.querySelector('[data-savings-row]');
      this.estimatedTotal = this.root.querySelector('[data-estimated-total]');
      this.primaryCta = this.root.querySelector('[data-primary-cta]');
      this.status = this.root.querySelector('[data-status]');
      this.exitButton = this.root.querySelector('[data-bundle-exit]');
      const firstCategoryButton = this.root.querySelector('[data-category]');
      this.categoryTabs =
        this.root.querySelector('.bundle-builder__tabs') ||
        firstCategoryButton?.parentElement ||
        null;
      this.categoryToolbar =
        this.root.querySelector('.bundle-builder__toolbar') ||
        this.categoryTabs?.parentElement ||
        null;
    }

    bindEvents() {
      this.primaryCta?.addEventListener('click', () => {
        if (this.state.step === 1) {
          this.goToStep(2);
        } else {
          this.addBundleToCheckout();
        }
      });

      this.mobileBackButton?.addEventListener('click', () => this.goToStep(1));

      this.mobileSummaryToggle?.addEventListener('click', () => {
        if (!this.getSelectedItems().length) return;
        this.setMobileSummaryExpanded(!this.state.summaryExpanded);
      });

      this.exitButton?.addEventListener('click', () => {
        try {
          this.clearPersistedState();
        } catch (error) {
          // Storage can be unavailable in privacy-restricted browsers.
        }
      });

      this.root.querySelectorAll('[data-step-button]').forEach((button) => {
        button.addEventListener('click', () => {
          this.goToStep(Number(button.dataset.stepButton));
        });
      });

      this.root.querySelectorAll('[data-category]').forEach((button) => {
        button.addEventListener('click', () => {
          this.state.category = button.dataset.category;
          this.root.querySelectorAll('[data-category]').forEach((item) => {
            item.classList.toggle('is-active', item === button);
          });
          this.renderAddons();
        });
      });

      document.addEventListener('bundle:checkout-return:cleared', (event) => {
        if (event.detail?.bundleHandle !== this.config.bundleHandle) return;
        this.resetAfterCheckoutReturn();
      });
    }

    resetAfterCheckoutReturn() {
      this.state.step = 1;
      this.state.primaryHandle = '';
      this.state.primaryVariantId = null;
      this.state.addons.clear();
      this.state.category = 'all';
      this.state.summaryExpanded = false;

      this.root.querySelectorAll('[data-category]').forEach((button) => {
        button.classList.toggle('is-active', button.dataset.category === 'all');
      });

      this.clearStatus();
      this.render();
    }

    get storageKey() {
      return `flycatcher.bundle.${this.config.bundleHandle}`;
    }

    get checkoutPendingKey() {
      return `${this.storageKey}.checkoutPending`;
    }

    clearPersistedState() {
      try {
        sessionStorage.removeItem(this.storageKey);
        sessionStorage.removeItem(this.checkoutPendingKey);
      } catch (error) {
        // Storage can be unavailable in privacy-restricted browsers.
      }
    }

    markCheckoutPending() {
      try {
        sessionStorage.setItem(
          this.checkoutPendingKey,
          JSON.stringify({
            bundleHandle: this.config.bundleHandle,
            startedAt: Date.now()
          })
        );
      } catch (error) {
        // Storage can be unavailable in privacy-restricted browsers.
      }
    }

    consumeCheckoutPending() {
      try {
        const marker = JSON.parse(
          sessionStorage.getItem(this.checkoutPendingKey)
        );

        if (!marker) return false;

        this.clearPersistedState();
        return true;
      } catch (error) {
        this.clearPersistedState();
        return false;
      }
    }

    saveState() {
      try {
        sessionStorage.setItem(
          this.storageKey,
          JSON.stringify({
            step: this.state.step,
            primaryHandle: this.state.primaryHandle,
            primaryVariantId: this.state.primaryVariantId,
            addons: Array.from(this.state.addons.entries()).map(([handle, item]) => ({
              handle,
              variantId: item.variantId,
              category: item.category
            }))
          })
        );
      } catch (error) {
        // Storage can be unavailable in privacy-restricted browsers.
      }
    }

    restoreState() {
      try {
        const saved = JSON.parse(sessionStorage.getItem(this.storageKey));

        if (!saved?.primaryHandle) return;

        const primaryOption = this.getPrimaryOption(saved.primaryHandle);
        if (!primaryOption?.product?.available) return;

        const primaryVariant = this.getVariant(primaryOption.product, saved.primaryVariantId);
        if (!primaryVariant?.available) return;

        this.state.primaryHandle = saved.primaryHandle;
        this.state.primaryVariantId = primaryVariant.id;

        const compatibleProducts = this.getCompatibleProducts(primaryOption);

        (saved.addons || []).forEach((savedAddon) => {
          const compatible = compatibleProducts.get(savedAddon.handle);
          if (!compatible) return;

          const variant = this.getVariant(compatible.product, savedAddon.variantId);
          if (!variant?.available) return;

          this.state.addons.set(savedAddon.handle, {
            product: compatible.product,
            variantId: variant.id,
            category: savedAddon.category || compatible.category
          });
        });

        this.state.step = saved.step === 2 ? 2 : 1;
      } catch (error) {
        // Ignore malformed or unavailable session storage.
      }
    }

    getPrimaryOption(handle = this.state.primaryHandle) {
      return this.config.primaryProducts.find((option) => option.product.handle === handle);
    }

    getVariant(product, variantId) {
      return product?.variants?.find((variant) => Number(variant.id) === Number(variantId)) ||
        product?.variants?.find((variant) => variant.available) ||
        product?.variants?.[0];
    }

    getCompatibleProducts(primaryOption = this.getPrimaryOption()) {
      const products = new Map();

      (primaryOption?.activities || []).forEach((product) => {
        if (product.available) products.set(product.handle, { product, category: 'activities' });
      });

      (primaryOption?.accessories || []).forEach((product) => {
        if (!product.available) return;

        if (products.has(product.handle)) {
          products.get(product.handle).category = 'all';
        } else {
          products.set(product.handle, { product, category: 'accessories' });
        }
      });

      return products;
    }

    render() {
      this.renderSteps();
      this.renderPrimaryProducts();
      this.renderAddons();
      this.renderSummary();
      this.renderCta();
    }

    renderSteps() {
      this.root.querySelectorAll('[data-step-panel]').forEach((panel) => {
        const isActive = Number(panel.dataset.stepPanel) === this.state.step;

        panel.hidden = !isActive;
        panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });

      this.root.querySelectorAll('[data-progress-step]').forEach((item) => {
        const step = Number(item.dataset.progressStep);
        const button = item.querySelector('[data-step-button]');
        const isActive = step === this.state.step;

        item.classList.toggle('is-active', step === this.state.step);
        item.classList.toggle('is-complete', step < this.state.step);

        if (button) {
          button.disabled = step === 2 && !this.state.primaryHandle;

          if (isActive) {
            button.setAttribute('aria-current', 'step');
          } else {
            button.removeAttribute('aria-current');
          }
        }
      });
    }

    renderPrimaryProducts() {
      if (!this.primaryGrid) return;

      this.primaryGrid.innerHTML = '';
      this.primaryGrid.classList.toggle(
        'is-single-item',
        this.config.primaryProducts.length === 1
      );

      this.config.primaryProducts.forEach((option) => {
        const selected = option.product.handle === this.state.primaryHandle;
        this.primaryGrid.appendChild(
          this.createProductCard(option.product, {
            selected,
            role: 'primary',
            category: 'primary'
          })
        );
      });
    }

    renderAddons() {
      if (!this.addonGroups || !this.state.primaryHandle) {
        this.updateCategoryTabs([]);
        if (this.addonGroups) this.addonGroups.innerHTML = '';
        return;
      }

      const option = this.getPrimaryOption();
      const groups = [
        {
          key: 'activities',
          title: this.config.labels.activities,
          products: option.activities || []
        },
        {
          key: 'accessories',
          title: this.config.labels.accessories,
          products: option.accessories || []
        }
      ];

      // Keep the category tabs in sync with the available compatible products.
      // This is intentionally calculated before search/filter rendering so a
      // tab does not disappear merely because the current search has no match.
      this.updateCategoryTabs(groups);

      const hasMultipleCategories = groups.filter((group) =>
        group.products.some((product) => product.available)
      ).length > 1;

      this.addonGroups.innerHTML = '';
      let visibleProductCount = 0;
      const renderedHandles = new Set();

      groups.forEach((group) => {
        if (this.state.category !== 'all' && this.state.category !== group.key) return;

        const products = group.products.filter((product) => {
          if (!product.available) return false;
          if (renderedHandles.has(product.handle)) return false;
          return true;
        });

        if (!products.length) return;

        const section = document.createElement('section');
        section.className = 'bundle-builder__group';

        if (hasMultipleCategories) {
          const heading = document.createElement('h3');
          heading.textContent = group.title;
          section.appendChild(heading);
        }

        const grid = document.createElement('div');
        grid.className = 'bundle-builder__grid';

        products.forEach((product) => {
          renderedHandles.add(product.handle);
          visibleProductCount += 1;

          grid.appendChild(
            this.createProductCard(product, {
              selected: this.state.addons.has(product.handle),
              role: 'addon',
              category: group.key
            })
          );
        });

        section.appendChild(grid);
        this.addonGroups.appendChild(section);
      });

      if (!visibleProductCount) {
        const empty = document.createElement('p');
        empty.className = 'bundle-builder__empty';
        empty.textContent = this.config.labels.empty;
        this.addonGroups.appendChild(empty);
      }
    }

    updateCategoryTabs(groups) {
      const availableCategories = new Set();

      groups.forEach((group) => {
        if (group.products.some((product) => product.available)) {
          availableCategories.add(group.key);
        }
      });

      // When only one category contains compatible products, the category
      // switcher is unnecessary. Keep the product list in All without an
      // additional category heading.
      const shouldShowTabs = availableCategories.size > 1;

      if (this.categoryToolbar) {
        // Hide the whole toolbar so there is no empty gap above the products.
        // Inline display is intentional because the theme stylesheet may set
        // .bundle-builder__toolbar or .bundle-builder__tabs to display:flex.
        this.categoryToolbar.hidden = !shouldShowTabs;
        this.categoryToolbar.style.display = shouldShowTabs ? '' : 'none';
        this.categoryToolbar.setAttribute(
          'aria-hidden',
          shouldShowTabs ? 'false' : 'true'
        );
      } else if (this.categoryTabs) {
        // Fallback for markup without the standard toolbar wrapper.

        // Use inline display as well as the hidden attribute because the
        // theme stylesheet may explicitly set .bundle-builder__tabs to flex.
        this.categoryTabs.style.display = shouldShowTabs ? '' : 'none';
        this.categoryTabs.hidden = !shouldShowTabs;
        this.categoryTabs.setAttribute(
          'aria-hidden',
          shouldShowTabs ? 'false' : 'true'
        );
      }

      this.root.querySelectorAll('[data-category]').forEach((button) => {
        const category = button.dataset.category;
        const shouldShow =
          category === 'all' || availableCategories.has(category);

        button.hidden = !shouldShow;
        button.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');

        if (!shouldShow) {
          button.classList.remove('is-active');
        }
      });

      // If the selected primary product has no products in the active
      // category, fall back to All so the customer never sees a dead tab.
      if (
        this.state.category !== 'all' &&
        !availableCategories.has(this.state.category)
      ) {
        this.state.category = 'all';
      }

      if (availableCategories.size <= 1) {
        this.state.category = 'all';
      }

      this.root.querySelectorAll('[data-category]').forEach((button) => {
        button.classList.toggle(
          'is-active',
          button.dataset.category === this.state.category && !button.hidden
        );
      });
    }

    createProductCard(product, options) {
      const card = document.createElement('article');
      const selectedVariantId = options.role === 'primary' && options.selected
        ? this.state.primaryVariantId
        : this.state.addons.get(product.handle)?.variantId;
      const variant = this.getVariant(product, selectedVariantId);
      const selectableVariants = product.variants.filter((item) => item.available);

      card.className = 'bundle-builder__card';
      card.dataset.productHandle = product.handle;
      card.classList.toggle('is-selected', options.selected);
      card.classList.toggle('is-unavailable', !variant?.available);

      const imageWrap = document.createElement('div');
      imageWrap.className = 'bundle-builder__image-wrap';

      if (variant?.image || product.image) {
        const image = document.createElement('img');
        image.src = variant?.image || product.image;
        image.alt = product.title;
        image.loading = 'lazy';
        imageWrap.appendChild(image);
      }

      const content = document.createElement('div');
      content.className = 'bundle-builder__card-content';

      const title = document.createElement('h3');
      title.className = 'bundle-builder__card-title';
      title.textContent = product.title;
      content.appendChild(title);

      const price = document.createElement('div');
      price.className = 'bundle-builder__card-price';
      price.textContent = this.formatMoney(variant?.price || 0);
      content.appendChild(price);

      let variantSelect = null;

      if (product.variants.length > 1 && selectableVariants.length > 0) {
        variantSelect = document.createElement('select');
        variantSelect.className = 'bundle-builder__variant-select';
        variantSelect.setAttribute('aria-label', `Choose an option for ${product.title}`);

        product.variants.forEach((item) => {
          const option = document.createElement('option');
          option.value = item.id;
          option.textContent = item.available ? item.title : `${item.title} - Sold out`;
          option.disabled = !item.available;
          option.selected = Number(item.id) === Number(variant?.id);
          variantSelect.appendChild(option);
        });

        variantSelect.addEventListener('change', () => {
          const nextVariant = this.getVariant(product, variantSelect.value);
          price.textContent = this.formatMoney(nextVariant.price);

          const image = imageWrap.querySelector('img');
          if (image && nextVariant.image) image.src = nextVariant.image;

          if (options.role === 'primary' && this.state.primaryHandle === product.handle) {
            this.state.primaryVariantId = nextVariant.id;
          }

          if (options.role === 'addon' && this.state.addons.has(product.handle)) {
            this.state.addons.get(product.handle).variantId = nextVariant.id;
          }

          this.saveState();
          this.renderSummary();
          this.renderCta();
        });

        content.appendChild(variantSelect);
      }

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'btn bundle-builder__select-button';
      button.disabled = !variant?.available;
      button.textContent = options.selected ? 'Selected' : '+ Add';
      button.setAttribute(
        'aria-label',
        `${options.selected ? 'Remove' : 'Add'} ${product.title}`
      );

      button.addEventListener('click', () => {
        const variantId = Number(variantSelect?.value || variant?.id);

        if (options.role === 'primary') {
          this.selectPrimary(product.handle, variantId);
        } else {
          this.toggleAddon(product, variantId, options.category);
        }
      });

      content.appendChild(button);
      card.appendChild(imageWrap);
      card.appendChild(content);

      return card;
    }

    selectPrimary(handle, variantId) {
      const changedProduct = this.state.primaryHandle && this.state.primaryHandle !== handle;

      this.state.primaryHandle = handle;
      this.state.primaryVariantId = variantId;

      if (changedProduct) {
        this.state.addons.clear();
        this.showStatus('Your add-on selections were cleared because the main product changed.');
      }

      this.saveState();
      this.renderSteps();
      this.renderPrimaryProducts();
      this.renderAddons();
      this.renderSummary();
      this.renderCta();

      this.track('bundle_primary_selected', {
        product_handle: handle,
        variant_id: variantId
      });
    }

    toggleAddon(product, variantId, category) {
      if (this.state.addons.has(product.handle)) {
        this.state.addons.delete(product.handle);
        this.track('bundle_addon_removed', { product_handle: product.handle });
      } else {
        this.state.addons.set(product.handle, { product, variantId, category });
        this.track('bundle_addon_added', {
          product_handle: product.handle,
          category
        });
      }

      this.saveState();
      this.renderAddons();
      this.renderSummary();
      this.renderCta();
    }

    goToStep(step) {
      if (step === 2 && !this.state.primaryHandle) return;

      this.state.step = step;
      this.state.summaryExpanded = false;
      this.saveState();
      this.renderSteps();
      this.renderSummary();
      this.renderCta();

      const activePanel = this.root.querySelector(
        `[data-step-panel="${this.state.step}"]`
      );

      activePanel?.scrollIntoView({ behavior: 'smooth', block: 'start' });

      this.track('bundle_step_viewed', { step });
    }

    renderSummary() {
      if (!this.summaryItems) return;

      this.summaryItems.innerHTML = '';
      const selectedItems = this.getSelectedItems();

      if (!this.state.primaryHandle) {
        this.summaryItems.appendChild(
          this.createSummaryPlaceholder('Main product', 'primary')
        );
      }

      selectedItems.forEach((item) => {
        this.summaryItems.appendChild(this.createSummaryItem(item));
      });

      const missingAddons = Math.max(
        Number(this.config.minimumAddons) - this.state.addons.size,
        0
      );

      for (let index = 0; index < missingAddons; index += 1) {
        const slotNumber = this.state.addons.size + index + 1;
        this.summaryItems.appendChild(
          this.createSummaryPlaceholder(`Item #${slotNumber}`, 'addon')
        );
      }

      const subtotal = selectedItems.reduce((total, item) => total + item.variant.price, 0);
      const unlocked = this.isRewardUnlocked();
      const showsSavings = unlocked && (
        this.config.rewardType === 'percentage' ||
        this.config.rewardType === 'fixed_amount_per_item'
      );
      const savings = this.getRewardSavings(selectedItems);

      this.subtotal.textContent = this.formatMoney(subtotal);
      this.savings.textContent = `-${this.formatMoney(savings)}`;
      this.savingsRow.hidden = !showsSavings;
      this.estimatedTotal.textContent = this.formatMoney(subtotal - savings);

      const supportsItemDiscount = this.config.rewardType === 'percentage' ||
        this.config.rewardType === 'fixed_amount_per_item';
      const previewSavings = supportsItemDiscount
        ? selectedItems.reduce(
          (total, item) => total + this.getItemRewardSavings(item, true),
          0
        )
        : 0;
      const headerSavings = showsSavings ? savings : previewSavings;

      if (this.desktopSummaryTotal) {
        this.desktopSummaryTotal.hidden = !selectedItems.length;
      }

      if (this.desktopCurrentTotal) {
        this.desktopCurrentTotal.textContent = this.formatMoney(subtotal - headerSavings);
        this.desktopCurrentTotal.classList.toggle('is-discounted', showsSavings);
        this.desktopCurrentTotal.classList.toggle(
          'is-pending',
          !showsSavings && headerSavings > 0
        );
      }

      if (this.desktopOriginalTotal) {
        this.desktopOriginalTotal.textContent = this.formatMoney(subtotal);
        this.desktopOriginalTotal.hidden = headerSavings <= 0;
      }

      this.renderReward();
      this.renderMobileSummary(selectedItems, subtotal, savings, unlocked);
    }

    renderMobileSummary(selectedItems, subtotal, savings, unlocked) {
      if (!this.summary) return;

      const hasPrimary = Boolean(this.state.primaryHandle);
      const isExpanded = this.state.summaryExpanded && hasPrimary;
      const isLocked = this.state.step === 2 &&
        this.config.enforceMinimum &&
        !unlocked;

      this.summary.classList.toggle('is-empty', !hasPrimary);
      this.summary.classList.toggle('is-expanded', isExpanded);
      this.summary.classList.toggle('is-locked', isLocked);
      this.summary.classList.toggle('is-unlocked', unlocked);
      this.summary.classList.toggle('is-step-one', this.state.step === 1);
      this.summary.classList.toggle('is-step-two', this.state.step === 2);

      if (this.mobileSummaryToggle) {
        this.mobileSummaryToggle.disabled = !hasPrimary;
        this.mobileSummaryToggle.setAttribute(
          'aria-expanded',
          isExpanded ? 'true' : 'false'
        );
      }

      if (this.mobileBackButton) {
        this.mobileBackButton.hidden = this.state.step !== 2;
      }

      if (this.mobileSummaryTotal) {
        this.mobileSummaryTotal.textContent = this.formatMoney(subtotal - savings);
        this.mobileSummaryTotal.hidden = !unlocked;
      }

      if (this.mobileSummaryOriginalTotal) {
        this.mobileSummaryOriginalTotal.textContent = this.formatMoney(subtotal);
        this.mobileSummaryOriginalTotal.hidden = !unlocked || savings <= 0;
      }

      if (this.mobileRewardBadge) {
        this.mobileRewardBadge.textContent = this.getRewardBadgeText();
        this.mobileRewardBadge.hidden = false;
        this.mobileRewardBadge.classList.toggle('is-unlocked', unlocked);
      }

      if (this.desktopRewardBadge) {
        this.desktopRewardBadge.textContent = this.getRewardBadgeText();
        this.desktopRewardBadge.classList.toggle('is-unlocked', unlocked);
      }

      this.renderMobileSummaryPreview(selectedItems);
      document.documentElement.classList.toggle('bundle-summary-open', isExpanded);
    }

    renderMobileSummaryPreview(selectedItems) {
      if (!this.mobileSummaryPreview) return;

      this.mobileSummaryPreview.innerHTML = '';

      const requiredSlots = Math.min(
        Math.max(Number(this.config.minimumAddons) + 1, 1),
        4
      );
      const hasOverflow = selectedItems.length > 4;
      const visibleItems = hasOverflow
        ? selectedItems.slice(0, 3)
        : selectedItems.slice(0, 4);

      visibleItems.forEach((item) => {
        const slot = document.createElement('span');
        slot.className = 'bundle-builder__mobile-preview-item';

        const image = document.createElement('img');
        image.src = item.variant.image || item.product.image || '';
        image.alt = '';
        image.loading = 'lazy';
        slot.appendChild(image);
        this.mobileSummaryPreview.appendChild(slot);
      });

      if (hasOverflow) {
        const more = document.createElement('span');
        more.className = 'bundle-builder__mobile-preview-item is-more';
        more.textContent = `+${selectedItems.length - 3}`;
        this.mobileSummaryPreview.appendChild(more);
        return;
      }

      const placeholders = Math.max(requiredSlots - visibleItems.length, 0);

      for (let index = 0; index < placeholders; index += 1) {
        const slot = document.createElement('span');
        slot.className = 'bundle-builder__mobile-preview-item is-placeholder';
        slot.textContent = '+';
        this.mobileSummaryPreview.appendChild(slot);
      }
    }

    getRewardBadgeText() {
      switch (this.config.rewardType) {
        case 'free_shipping':
          return 'Free\nshipping';
        case 'free_gift':
          return 'Free\ngift';
        case 'fixed_amount_per_item':
        case 'percentage':
        default:
          return `${this.config.rewardValue}%\noff`;
      }
    }

    setMobileSummaryExpanded(expanded) {
      this.state.summaryExpanded = Boolean(expanded);

      const isExpanded = this.state.summaryExpanded && Boolean(this.state.primaryHandle);
      this.summary?.classList.toggle('is-expanded', isExpanded);
      this.mobileSummaryToggle?.setAttribute(
        'aria-expanded',
        isExpanded ? 'true' : 'false'
      );
      document.documentElement.classList.toggle('bundle-summary-open', isExpanded);
    }

    createSummaryItem(item) {
      const row = document.createElement('div');
      row.className = 'bundle-builder__summary-item';
      row.classList.add(`is-${item.role}`);

      const image = document.createElement('img');
      image.src = item.variant.image || item.product.image || '';
      image.alt = '';
      image.loading = 'lazy';

      const info = document.createElement('div');
      const title = document.createElement('p');
      title.className = 'bundle-builder__summary-item-title';
      title.textContent = item.product.title;
      title.title = item.product.title;
      info.appendChild(title);

      if (item.product.variants.length > 1) {
        const variantTitle = document.createElement('small');
        variantTitle.textContent = item.variant.title;
        info.appendChild(variantTitle);
      }

      const pricing = document.createElement('div');
      pricing.className = 'bundle-builder__summary-item-pricing';

      const potentialSavings = this.getItemRewardSavings(item, true);
      const discountApplied = this.isRewardUnlocked() && potentialSavings > 0;
      const discountAvailable = potentialSavings > 0;
      const currentPrice = document.createElement('strong');
      currentPrice.className = 'bundle-builder__summary-item-price';

      if (discountAvailable) {
        currentPrice.classList.add('is-discounted');
        currentPrice.classList.toggle('is-pending', !discountApplied);
        currentPrice.textContent = this.formatMoney(item.variant.price - potentialSavings);

        const originalPrice = document.createElement('s');
        originalPrice.className = 'bundle-builder__summary-item-original-price';
        originalPrice.textContent = this.formatMoney(item.variant.price);

        pricing.setAttribute(
          'aria-label',
          discountApplied
            ? `${this.formatMoney(item.variant.price - potentialSavings)}, originally ${this.formatMoney(item.variant.price)}`
            : `${this.formatMoney(item.variant.price - potentialSavings)} bundle price after unlock, currently ${this.formatMoney(item.variant.price)}`
        );

        pricing.appendChild(currentPrice);
        pricing.appendChild(originalPrice);
      } else {
        currentPrice.textContent = this.formatMoney(item.variant.price);
        pricing.appendChild(currentPrice);
      }

      row.appendChild(image);
      row.appendChild(info);
      row.appendChild(pricing);

      if (item.role === 'addon') {
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'bundle-builder__summary-remove';
        remove.setAttribute('aria-label', `Remove ${item.product.title}`);
        remove.innerHTML = '<span aria-hidden="true">&times;</span>';
        remove.addEventListener('click', () => this.toggleAddon(
          item.product,
          item.variant.id,
          item.category
        ));
        row.appendChild(remove);
      }

      return row;
    }

    createSummaryPlaceholder(label, role) {
      const row = document.createElement('div');
      row.className = `bundle-builder__summary-placeholder is-${role}`;

      const icon = document.createElement('span');
      icon.className = 'bundle-builder__summary-placeholder-icon';
      icon.setAttribute('aria-hidden', 'true');

      const text = document.createElement('span');
      text.className = 'bundle-builder__summary-placeholder-label';
      text.textContent = label;

      row.appendChild(icon);
      row.appendChild(text);
      return row;
    }

    getSelectedItems() {
      const items = [];
      const primaryOption = this.getPrimaryOption();

      if (primaryOption) {
        const variant = this.getVariant(primaryOption.product, this.state.primaryVariantId);
        if (variant?.available) {
          items.push({
            product: primaryOption.product,
            variant,
            role: 'primary',
            category: 'primary'
          });
        }
      }

      this.state.addons.forEach((addon) => {
        const variant = this.getVariant(addon.product, addon.variantId);
        if (!variant?.available) return;

        items.push({
          product: addon.product,
          variant,
          role: 'addon',
          category: addon.category
        });
      });

      return items;
    }

    isRewardUnlocked() {
      return this.state.addons.size >= Number(this.config.minimumAddons);
    }

    getRewardSavings(selectedItems) {
      if (!this.isRewardUnlocked()) return 0;

      if (this.config.rewardType === 'percentage') {
        return selectedItems.reduce(
          (total, item) => total + this.getItemRewardSavings(item),
          0
        );
      }

      if (this.config.rewardType === 'fixed_amount_per_item') {
        return selectedItems.reduce(
          (total, item) => total + this.getItemRewardSavings(item),
          0
        );
      }

      return 0;
    }

    getItemRewardSavings(item, includeLocked = false) {
      const supportsItemDiscount = this.config.rewardType === 'percentage' ||
        this.config.rewardType === 'fixed_amount_per_item';

      if (!supportsItemDiscount) return 0;
      if (!includeLocked && !this.isRewardUnlocked()) return 0;

      const price = Number(item?.variant?.price || 0);
      const percentage = Number(this.config.rewardValue) / 100;
      const rawSavings = price * percentage;

      return this.config.rewardType === 'percentage'
        ? Math.floor(rawSavings + 1e-9)
        : Math.round(rawSavings);
    }

    renderReward() {
      const remaining = Math.max(
        Number(this.config.minimumAddons) - this.state.addons.size,
        0
      );
      const unlocked = this.isRewardUnlocked();

      this.reward.classList.toggle('is-unlocked', unlocked);

      if (!this.state.primaryHandle) {
        this.reward.textContent = 'Choose a main product to continue.';
        return;
      }

      if (unlocked) {
        switch (this.config.rewardType) {
          case 'free_shipping':
            this.reward.textContent = 'Bundle unlocked! Free shipping will be applied when eligible.';
            break;
          case 'free_gift':
            this.reward.textContent = 'Bundle unlocked! Your free gift will be added at checkout.';
            break;
          case 'fixed_amount_per_item':
            this.reward.textContent = `Bundle unlocked! You save ${this.config.rewardValue}% on each bundle product.`;
            break;
          case 'percentage':
          default:
            this.reward.textContent = `Bundle unlocked! You save ${this.config.rewardValue}%.`;
            break;
        }
      } else {
        const noun = remaining === 1 ? 'product' : 'products';
        let reward;

        switch (this.config.rewardType) {
          case 'free_shipping':
            reward = 'free shipping';
            break;
          case 'free_gift':
            reward = 'a free gift';
            break;
          case 'fixed_amount_per_item':
            reward = `${this.config.rewardValue}% off each bundle product`;
            break;
          case 'percentage':
          default:
            reward = `${this.config.rewardValue}% off`;
            break;
        }

        this.reward.textContent = `Add ${remaining} more ${noun} to unlock ${reward}.`;
      }
    }

    renderCta() {
      if (!this.primaryCta) return;

      if (this.state.step === 1) {
        this.primaryCta.textContent = this.config.labels.next;
        this.primaryCta.disabled = !this.state.primaryHandle;
        this.primaryCta.classList.remove('is-reward-locked');
        return;
      }

      const remaining = Math.max(
        Number(this.config.minimumAddons) - this.state.addons.size,
        0
      );

      const rewardLocked = remaining > 0 && this.config.enforceMinimum;

      this.primaryCta.textContent = rewardLocked
        ? `Add ${remaining} more to continue`
        : this.config.labels.add;

      this.primaryCta.disabled = !this.state.primaryHandle ||
        rewardLocked;
      this.primaryCta.classList.toggle('is-reward-locked', rewardLocked);
    }

    async createStorefrontCart(lines) {
      const apiUrl =
        this.config.storefrontApiUrl ||
        '/api/2026-07/graphql.json';

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          query: `
            mutation CreateBundleCart($input: CartInput!) {
              cartCreate(input: $input) {
                cart {
                  id
                  checkoutUrl
                  totalQuantity
                }
                userErrors {
                  field
                  message
                }
                warnings {
                  code
                  message
                }
              }
            }
          `,
          variables: {
            input: {
              lines
            }
          }
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(
          `Storefront API request failed (${response.status}).`
        );
      }

      if (result.errors?.length) {
        throw new Error(
          result.errors.map((error) => error.message).join('; ')
        );
      }

      const payload = result.data?.cartCreate;

      if (!payload) {
        throw new Error('Storefront API returned no cart.');
      }

      if (payload.userErrors?.length) {
        throw new Error(
          payload.userErrors.map((error) => error.message).join('; ')
        );
      }

      if (!payload.cart?.checkoutUrl) {
        throw new Error('Shopify did not return a checkout URL.');
      }

      return payload.cart;
    }

    async addBundleToCheckout() {
      if (!this.state.primaryHandle) return;
      if (this.config.enforceMinimum && !this.isRewardUnlocked()) return;

      const selectedItems = this.getSelectedItems();

      if (!selectedItems.length) return;

      const bundleId = this.createBundleId();

      const lines = selectedItems.map((item) => ({
        merchandiseId:
          `gid://shopify/ProductVariant/${item.variant.id}`,
        quantity: 1,
        attributes: this.bundleAttributes(
          bundleId,
          item.role,
          this.config.rewardType
        )
      }));

      const shouldAddGift =
        this.config.rewardType === 'free_gift' &&
        this.isRewardUnlocked();

      if (shouldAddGift) {
        const giftVariant = this.config.freeGiftVariant;

        if (!giftVariant?.id || giftVariant.available !== true) {
          this.showStatus(
            'The free gift is currently unavailable. Please try again later.',
            true
          );
          return;
        }

        lines.push({
          merchandiseId:
            `gid://shopify/ProductVariant/${giftVariant.id}`,
          quantity: 1,
          attributes: this.bundleAttributes(
            bundleId,
            'gift',
            'free_gift'
          )
        });
      }

      this.setLoading(true);
      this.clearStatus();

      try {
        const storefrontCart =
          await this.createStorefrontCart(lines);

        this.track('bundle_added_to_cart', {
          primary_product: this.state.primaryHandle,
          addon_count: this.state.addons.size,
          reward_type: this.config.rewardType,
          reward_value: this.config.rewardValue,
          free_gift_added: shouldAddGift,
          storefront_cart_id: storefrontCart.id
        });

        this.track('bundle_checkout_started', {
          bundle_id: bundleId,
          primary_product: this.state.primaryHandle,
          addon_count: this.state.addons.size,
          reward_type: this.config.rewardType,
          free_gift_added: shouldAddGift,
          storefront_cart_id: storefrontCart.id
        });

        this.markCheckoutPending();
        window.location.assign(storefrontCart.checkoutUrl);
      } catch (error) {
        this.showStatus(
          error.message ||
            'Unable to create checkout. Please try again.',
          true
        );

        this.track('bundle_add_to_cart_error', {
          message: error.message || 'Unknown error'
        });
      } finally {
        this.setLoading(false);
      }
    }

    bundleAttributes(
      bundleId,
      role,
      rewardType = this.config.rewardType
    ) {
      return [
        {
          key: '_bundle_id',
          value: bundleId
        },
        {
          key: '_bundle_offer_key',
          value: this.config.bundleHandle
        },
        {
          key: '_bundle_name',
          value: this.config.bundleName || ''
        },
        {
          key: '_bundle_role',
          value: role
        },
        {
          key: '_bundle_primary',
          value: this.state.primaryHandle
        },
        {
          key: '_bundle_reward_type',
          value: rewardType
        }
      ];
    }

    createBundleId() {
      if (window.crypto?.randomUUID) {
        return `${this.config.bundleHandle}-${window.crypto.randomUUID()}`;
      }

      return `${this.config.bundleHandle}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }

    setLoading(loading) {
      this.root.setAttribute('aria-busy', loading ? 'true' : 'false');
      this.primaryCta.classList.toggle('btn--loading', loading);
      this.primaryCta.disabled = loading ||
        !this.state.primaryHandle ||
        (this.config.enforceMinimum && this.state.step === 2 && !this.isRewardUnlocked());
    }

    showStatus(message, isError = false) {
      if (!this.status) return;
      this.status.textContent = message;
      this.status.classList.toggle('is-error', isError);
    }

    clearStatus() {
      if (!this.status) return;
      this.status.textContent = '';
      this.status.classList.remove('is-error');
    }

    formatMoney(cents) {
      if (window.theme?.Currency?.formatMoney && window.theme?.settings?.moneyFormat) {
        return window.theme.Currency.formatMoney(cents, window.theme.settings.moneyFormat);
      }

      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD'
      }).format(Number(cents || 0) / 100);
    }

    track(event, data = {}) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event,
        bundle_type: this.config.bundleHandle,
        ...data
      });
    }
  }

  window.FlycatcherBundleBuilder = FlycatcherBundleBuilder;

  const initialize = (scope = document) => {
    scope.querySelectorAll('[data-bundle-builder]').forEach((element) => {
      if (element.dataset.bundleBuilderInitialized === 'true') return;
      element.dataset.bundleBuilderInitialized = 'true';
      new FlycatcherBundleBuilder(element);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
